import React, { useRef } from 'react';
import { Select, Grid, Dropdown, Menu, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { AudioOutlined } from '@ant-design/icons';
import './MicrophoneSelector.css';

const { Option } = Select;
const { useBreakpoint } = Grid;

const MicrophoneSelector = ({ devices, selectedDevice, onDeviceChange }) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const selectRef = useRef(null);

  if (!devices || devices.length === 0) {
    return null;
  }

  const handleDeviceChange = (value) => {
    onDeviceChange(value);
    // Remove focus from the selector after a change
    if (selectRef.current) {
      selectRef.current.blur();
    }
  };



  const menuItems = devices.map((device, index) => ({
    key: device.deviceId,
    label: device.label || `Microphone ${index + 1}`,
    onClick: () => handleDeviceChange(device.deviceId)
  }));

  if (screens.xs) {
    return null;
  }

  // Normalize labels to avoid duplicate entries like "Predeterminado - X" vs "X"
  const normalizeLabel = (label) => {
    if (!label || typeof label !== 'string') return '';
    return label
      .replace(/^\s*Default\s*-\s*/i, '')
      .replace(/^\s*Predeterminado\s*-\s*/i, '')
      .replace(/\s*\(predeterminado\)\s*$/i, '')
      .trim();
  };

  const visibleDevices = [];
  const seenByLabel = new Set();
  for (const device of devices) {
    // Skip the synthetic 'default' device to avoid duplicate entries with the same underlying mic
    if (device.deviceId === 'default') continue;
    const label = device.label || '';
    const normalized = normalizeLabel(label) || label || t('microphone_selector.unknown');
    if (seenByLabel.has(normalized.toLowerCase())) continue;
    seenByLabel.add(normalized.toLowerCase());
    visibleDevices.push({ id: device.deviceId, label: normalized });
  }

  // Ensure the current value has a friendly label even if it's 'default'
  const currentValueIsDefault = selectedDevice === 'default';
  const defaultOption = currentValueIsDefault ? (
    <Option
      key="default"
      value="default"
      label={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%' }}>
          <AudioOutlined />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t('microphone_selector.system_default', 'System default')}
          </span>
        </span>
      )}
    >
      {t('microphone_selector.system_default', 'System default')}
    </Option>
  ) : null;

  return (
    <Select
      ref={selectRef}
      className="microphone-selector"
      value={selectedDevice}
      onChange={handleDeviceChange}
      popupMatchSelectWidth={false}
      optionLabelProp="label"
      style={{ minWidth: 200 }}
    >
      {defaultOption}
      {visibleDevices.map((d, index) => (
        <Option
          key={d.id}
          value={d.id}
          label={(
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%' }}>
              <AudioOutlined />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.label || `Microphone ${index + 1}`}
              </span>
            </span>
          )}
        >
          {d.label || `Microphone ${index + 1}`}
        </Option>
      ))}
    </Select>
  );
};

export default MicrophoneSelector;
